import {
    AssessmentGroupWriteModel,
    AssessmentWriteModel,
    IAssessmentGroupWriteModel,
    IAssessmentWriteModel,
    CommentEntryModel,
    ManualMarkEntryModel,
    MarkGridCellModel,
    MarkGridColumnEntriesModel,
    MarkGridColumnModel,
    MarkGridCommentColumnModel,
    MarkGridCommentSaveRequest,
    MarkGridCommentSaveResultModel,
    MarkGridCommentsModel,
    MarkGridCreatedModel,
    MarkGridDeletedModel,
    MarkGridGroupTypeCoverageModel,
    MarkGridSiblingTermModel,
    MarkGridSubjectListModel,
    MarkGridSubjectModel,
    MarkGridTermModel,
    MarkGridNodeModel,
    MarkGridOverviewModel,
    MarkGridRowModel,
    MarkGridSaveRequest,
    MarkGridSaveResultModel,
    MarkGridSheetModel,
    MarkGridSheetRequest,
} from "../api/APIClient";
import { markGridClient } from "../api/clients";

/**
 * The mark grid: a teacher's subjects (each once, opening on its current term), a tile overview of one term of a
 * subject or of a division inside it, a spreadsheet of a division's assessments with the division and module marks
 * beside them, and the subject comments for the term.
 *
 * Division and module columns are stored results the calculation engine wrote, and division weights come from the
 * generated calculation edges - this store never works either out. That is the "calculate once, store forever"
 * invariant: if a total here looks wrong, the fix is in the procedure, not in the screen.
 */

export type {
    MarkGridCellModel,
    MarkGridColumnModel,
    MarkGridCreatedModel,
    MarkGridDeletedModel,
    MarkGridGroupTypeCoverageModel,
    MarkGridCommentsModel,
    MarkGridSiblingTermModel,
    MarkGridSubjectListModel,
    MarkGridSubjectModel,
    MarkGridTermModel,
    MarkGridNodeModel,
    MarkGridOverviewModel,
    MarkGridRowModel,
    MarkGridSaveResultModel,
    MarkGridSheetModel,
};

export const ColumnKinds = {
    Assessment: "Assessment",
    Subtotal: "Subtotal",
    GroupTotal: "GroupTotal",
    ModuleTotal: "ModuleTotal",
} as const;

/**
 * The caller's own subjects, each once with its terms and the term it opens on; `all` lists every subject (and is what
 * someone with no classes yet gets anyway).
 */
export const listSubjects = (all: boolean): Promise<MarkGridSubjectListModel> => markGridClient.subjects(all);

/**
 * Deletes an assessment created by mistake, with its calculation edges. When it already has marks or comments,
 * `removeMarks` must confirm removing them (and removing marks needs the delete-marks permission).
 */
export const deleteAssessment = (assessmentID: string, removeMarks: boolean): Promise<MarkGridDeletedModel> =>
    markGridClient.deleteAssessment(assessmentID, removeMarks);

/** Subject comments for one term: every comment type side by side, one comment per student per type. */
export const getComments = (moduleID: string, teachingGroupID?: string): Promise<MarkGridCommentsModel> =>
    markGridClient.comments(moduleID, teachingGroupID);

export interface CommentColumn {
    commentTypeID: string;
    /** Blank text clears that student's comment. */
    entries: Array<{ studentID: string; result: string }>;
}

/** Saves the edited comments of one term; each comment type is its own server-side transaction. */
export const saveComments = (moduleID: string, columns: CommentColumn[], teachingGroupID?: string): Promise<MarkGridCommentSaveResultModel> =>
    markGridClient.saveComments(
        new MarkGridCommentSaveRequest({
            qualificationModuleID: moduleID,
            teachingGroupID,
            columns: columns.map(
                (c) =>
                    new MarkGridCommentColumnModel({
                        commentTypeID: c.commentTypeID,
                        entries: c.entries.map((e) => new CommentEntryModel({ studentID: e.studentID, result: e.result })),
                    })
            ),
        })
    );

export const getOverview = (moduleID: string, groupID?: string, teachingGroupID?: string): Promise<MarkGridOverviewModel> =>
    markGridClient.overview(moduleID, groupID, teachingGroupID);

export const getGroupTypeCoverage = (moduleID: string): Promise<MarkGridGroupTypeCoverageModel[]> =>
    markGridClient.groupTypeCoverage(moduleID);

export interface SheetQuery {
    moduleID: string;
    groupID?: string;
    teachingGroupID?: string;
    includeInactive?: boolean;
    /** Only needed when the module's procedures leave the capture mark type ambiguous. */
    entryMarkTypeID?: string;
}

export const getSheet = (query: SheetQuery): Promise<MarkGridSheetModel> =>
    markGridClient.sheet(
        new MarkGridSheetRequest({
            qualificationModuleID: query.moduleID,
            groupID: query.groupID,
            teachingGroupID: query.teachingGroupID,
            includeInactive: query.includeInactive,
            entryMarkTypeID: query.entryMarkTypeID,
        })
    );

export interface ColumnEntries {
    entityType: string;
    entityID: string;
    markTypeID: string;
    entries: Array<{ studentID: string; result: number }>;
}

/**
 * Saves every edited column. Assessment entries never carry a total - the API takes it from the assessment - and
 * each column is its own transaction server-side, so a failure part-way reports how many marks made it.
 */
export const saveMarks = (columns: ColumnEntries[], teachingGroupID?: string): Promise<MarkGridSaveResultModel> =>
    markGridClient.save(
        new MarkGridSaveRequest({
            teachingGroupID,
            columns: columns.map(
                (c) =>
                    new MarkGridColumnEntriesModel({
                        targetEntityType: c.entityType,
                        targetEntityID: c.entityID,
                        markTypeID: c.markTypeID,
                        entries: c.entries.map((e) => new ManualMarkEntryModel({ studentID: e.studentID, result: e.result })),
                    })
            ),
        })
    );

/** Adds an assessment to a division and regenerates the module's procedures so it counts straight away. */
export const createAssessment = (model: IAssessmentWriteModel): Promise<MarkGridCreatedModel> =>
    markGridClient.createAssessment(new AssessmentWriteModel(model));

/**
 * Adds a division. With a parent the owning module is deliberately not sent - the API derives it from the parent,
 * the same rule as the structure editor.
 */
export const createDivision = (model: IAssessmentGroupWriteModel): Promise<MarkGridCreatedModel> =>
    markGridClient.createGroup(
        new AssessmentGroupWriteModel(model.parentGroupID ? { ...model, qualificationModuleID: undefined } : model)
    );

export const formatMark = (value?: number | null, digits = 1): string =>
    value === undefined || value === null ? "-" : Number(value).toFixed(digits).replace(/\.0+$/, "");

/** A stored result as a percentage of its own total, which is how division and module marks read. */
export const percentOf = (cell?: MarkGridCellModel): number | undefined =>
    cell?.result === undefined || cell?.result === null || !cell?.total ? undefined : (cell.result / cell.total) * 100;
